import { ImagePicker, PageHeader } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Droplet } from "lucide-react";
import { type ReactNode, useState } from "react";
import { accountActions, imageProblem } from "../../account";
import { errorOf } from "../../api";
import { messageFor } from "../../error-copy";
import { SETTINGS_MENU } from "../../nav";
import { type Me, meQuery } from "../../session";
import { useSetMe } from "../account";
import styles from "./settings.module.css";

const item = SETTINGS_MENU.flatMap((s) => s.items).find((i) => i.id === "branding")!;

/**
 * Settings › Company › Branding (P8-54): the company's two logos. Needs `company.manage`. The
 * square mark is the sidebar's; the full, horizontal logo is for documents and emails (P8-55).
 */
export function BrandingPage(): ReactNode {
  // the console's route only renders once someone is signed in, in a company
  const company = useSuspenseQuery(meQuery).data!.active_company!;
  return (
    <>
      <PageHeader title={item.label} description={item.description} />
      <LogoSlot
        title="Mark"
        noun="mark"
        shape="square"
        src={company.logo_url}
        upload={accountActions.setLogo}
        remove={accountActions.removeLogo}
        note={`A square icon. It replaces the drop at the top of the sidebar for everyone in ${company.name}; the console keeps its centre square.`}
      />
      <LogoSlot
        title="Full logo"
        noun="full logo"
        shape="wide"
        src={company.logo_full_url}
        upload={accountActions.setFullLogo}
        remove={accountActions.removeFullLogo}
        note="Your horizontal logo, kept whole: a PNG with a transparent background works best. It heads your invoices, delivery notes and emails."
      />
    </>
  );
}

function LogoSlot(props: {
  title: string;
  noun: string;
  shape: "square" | "wide";
  src: string | null;
  upload: (file: File) => Promise<Me>;
  remove: () => Promise<Me>;
  note: string;
}) {
  const setMe = useSetMe();
  const [error, setError] = useState<string>();
  const run = async (action: () => Promise<Me>) => {
    setError(undefined);
    try {
      setMe(await action());
    } catch (e) {
      const failure = errorOf(e);
      setError(imageProblem(failure.code) ?? messageFor(failure));
      throw e;
    }
  };
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{props.title}</h2>
      <ImagePicker
        noun={props.noun}
        shape={props.shape}
        src={props.src}
        fallback={<Droplet size={24} aria-hidden="true" />}
        onUpload={(file) => run(() => props.upload(file))}
        onRemove={() => run(props.remove)}
      />
      {error ? (
        <Alert tone="danger" title={`${props.title} not saved`}>
          {error}
        </Alert>
      ) : null}
      <p className={styles.muted}>{props.note}</p>
    </section>
  );
}
